import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { track } from './utility/analytics';

import { useDispatch, useSelector } from 'react-redux';
import { initKeycloak, getKeycloakInstance } from './utility/keycloak';
import PrivateRoute from './components/PrivateRoute';
import HomePage from './pages/HomePage';
import UserPage from './pages/UserPage';
import McpPage from './pages/McpPage';
import FreeTierPage from './pages/FreeTierPage';
import CategoryPage from './pages/CategoryPage';
import NotFoundPage from './pages/NotFoundPage';
import Navbar from './components/Navbar';
import NotificationContainer from './components/NotificationContainer';
import ComingSoon from './pages/ComingSoon';
import CreateInstancePage from './pages/CreateInstancePage';
import PaidInstanceLaunch from './components/PaidInstanceLaunch';
import ServerErrorPage from './pages/ServerErrorPage';
import InstanceDetailsPage from './pages/InstanceDetailsPage';
import SnapshotsPage from './pages/SnapshotsPage';
import BackupsPage from './pages/BackupsPage';
import AdminPage from './pages/AdminPage';
import StartPage from './pages/StartPage';

import { RootState, AppDispatch } from './store/store';

import { fetchInstances } from './store/slices/instancesSlice';
import { fetchImages } from './store/slices/imagesSlice';
import { fetchFlavors } from './store/slices/flavorsSlice';
import { fetchSecurityGroups } from './store/slices/securityGroupsSlice';
import { fetchPrivateNetwork } from './store/slices/privateNetworkSlice';
import { connectSocket } from './utility/socketService';
import { fetchSecurityGroupRules } from './store/slices/securityRulesSlice';
import { fetchVolumes } from './store/slices/volumesSlice';
import { fetchBillingForecast, fetchBudgetState } from './store/slices/billingSlice';
import TrialBanner from './components/TrialBanner';
import RegistrationNotices from './components/RegistrationNotices';
import { fetchSnapshots } from './store/slices/snapshotsSlice';
import { fetchBackups } from './store/slices/backupsSlice';
import { fetchLoadBalancers } from './store/slices/loadBalancersSlice';
import { parseKeycloakToken } from './utility/parseKeycloakToken';
import { logout as logoutAction, setAuthenticated, setProvisioningTimedOut } from './store/slices/authSlice';
import { fetchRegions } from './store/slices/regionsSlice';
import { fetchKeypairs } from './store/slices/keypairsSlice';

import './styles/App.scss';

/**
 * Clarity helper: extract common identifiers/tags without knowing exact userInfo shape.
 * This is intentionally defensive.
 */
function getFirstString(obj: any, paths: string[]): string | undefined {
  for (const p of paths) {
    const parts = p.split('.');
    let cur = obj;
    for (const part of parts) {
      if (!cur || typeof cur !== 'object') { cur = undefined; break; }
      cur = cur[part];
    }
    if (typeof cur === 'string' && cur.trim()) return cur;
    if (typeof cur === 'number') return String(cur);
  }
  return undefined;
}

function getFirstStringFromArray(obj: any, paths: string[]): string | undefined {
  for (const p of paths) {
    const v = getFirstValue(obj, p);
    if (Array.isArray(v) && v.length) {
      const first = v[0];
      if (typeof first === 'string' && first.trim()) return first;
    }
  }
  return undefined;
}

function getFirstValue(obj: any, path: string): any {
  const parts = path.split('.');
  let cur = obj;
  for (const part of parts) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = cur[part];
  }
  return cur;
}

function safeClarityIdentifyAndTag(userInfo: any) {
  if (!window.clarity || !userInfo) return;

  // Try common stable user identifiers (Keycloak/JWT-ish)
  const userId =
    getFirstString(userInfo, [
      'id',
      'user_id',
      'sub',
      'uid',
      'account_id',
      'preferred_username', // fallback
      'username',
    ]);

  if (userId) {
    try { window.clarity('identify', userId); } catch (_) { /* empty */ }
  }

  // Tags (only set if present)
  const role =
    getFirstString(userInfo, ['role']) ||
    getFirstStringFromArray(userInfo, ['roles', 'realm_access.roles', 'resource_access.account.roles']);

  const tenant =
    getFirstString(userInfo, ['tenant', 'tenant_id', 'project', 'project_id', 'os_project_id', 'openstack.project_id']);

  const region =
    getFirstString(userInfo, ['region', 'default_region', 'os_region', 'openstack.region']);

  const plan =
    getFirstString(userInfo, ['plan', 'subscription', 'billing_plan']);

  try {
    if (role) window.clarity('set', 'role', role);
    if (tenant) window.clarity('set', 'tenant', tenant);
    if (region) window.clarity('set', 'region', region);
    if (plan) window.clarity('set', 'plan', plan);

    // Helpful static tag
    window.clarity('set', 'app', 'console');
  } catch (_) { /* empty */ }
}

/**
 * Route tracker for:
 * - your existing recentPages list
 * - Clarity SPA page tracking
 */
const RouteTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname + location.search;

    // Clarity SPA page tracking
    try {
      if (window.clarity) window.clarity('set', 'page', path);
    } catch (_) { /* empty */ }

    // Your existing recentPages logic
    try {
      const raw = localStorage.getItem('recentPages');
      let recent: { path: string; title?: string }[] = raw ? JSON.parse(raw) : [];
      if (recent[0]?.path === path) return;
      recent = recent.filter(p => p.path !== path);
      const title = document?.title || undefined;
      recent.unshift({ path, title });
      if (recent.length > 5) recent = recent.slice(0, 5);
      localStorage.setItem('recentPages', JSON.stringify(recent));
    } catch (e) {
      // fail silently
    }
  }, [location]);

  return null;
};

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const theme = useSelector((state: RootState) => state.ui.theme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Inject Umami analytics script if configured (runs once on mount)
  useEffect(() => {
    const umamiUrl = process.env.REACT_APP_UMAMI_URL;
    const umamiId  = process.env.REACT_APP_UMAMI_WEBSITE_ID;
    if (!umamiUrl || !umamiId || process.env.NODE_ENV !== 'production' || document.getElementById('umami-script')) return;
    const script = document.createElement('script');
    script.id                 = 'umami-script';
    script.src                = `${umamiUrl}/script.js`;
    script.defer              = true;
    script.dataset.websiteId  = umamiId;
    script.onerror = () => { /* noop */ };  // silence load errors - analytics is non-critical
    document.head.appendChild(script);
  }, []);
  const osReady = useSelector((state: RootState) => state.auth.userInfo?.os_ready ?? false);
  const provisioningTimedOut = useSelector((state: RootState) => state.auth.provisioningTimedOut);

  // We don't assume shape, but we pass it to Clarity helper:
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);

  const instancesLoaded = useSelector((state: RootState) => state.instances.status !== 'idle');
  const imagesLoaded = useSelector((state: RootState) => state.images.status !== 'idle');
  const flavorsLoaded = useSelector((state: RootState) => state.flavors.status !== 'idle');
  const securityGroupsLoaded = useSelector((state: RootState) => state.securityGroups.status !== 'idle');
  const securityRulesLoaded = useSelector((state: RootState) => state.securityRules.status !== 'idle');
  const privateNetworkLoaded = useSelector((state: RootState) => state.privateNetwork.status !== 'idle');
  const volumesLoaded = useSelector((state: RootState) => state.volumes.status !== 'idle');
  const billingLoaded = useSelector((state: RootState) => state.billingForecast.status !== 'idle');
  const budgetStateLoaded = useSelector((state: RootState) => state.billingForecast.budgetStatus !== 'idle');
  const snapshotsLoaded = useSelector((state: RootState) => state.snapshots.status !== 'idle');
  const backupsLoaded = useSelector((state: RootState) => state.backups.status !== 'idle');
  const loadBalancersLoaded = useSelector((state: RootState) => state.loadBalancers.status !== 'idle');
  const regionsLoaded = useSelector((state: RootState) => state.regions.status !== 'idle');
  const keypairsLoaded = useSelector((state: RootState) => state.keypairs.status !== 'idle');

  const [keycloakReady, setKeycloakReady] = useState(false);
  const [keycloakError, setKeycloakError] = useState(false);

  // 1) Init Keycloak
  useEffect(() => {
    const initializeKeycloak = async () => {
      try {
        await initKeycloak(setKeycloakReady);
        setKeycloakReady(true);
      } catch (error) {
        console.error('Keycloak initialization failed', error);
        setKeycloakError(true);
      }
    };

    initializeKeycloak();
  }, []);

  // 2) Connect websocket when ready
  useEffect(() => {
    if (keycloakReady && isAuthenticated && osReady) {
      connectSocket();
      track('login');
    }
  }, [keycloakReady, isAuthenticated, osReady]);

  // 3) Fetch app data after auth+ready
  useEffect(() => {
    if (!keycloakReady || !isAuthenticated || !osReady) return;

    if (!instancesLoaded) dispatch(fetchInstances());
    if (!imagesLoaded) dispatch(fetchImages());
    if (!flavorsLoaded) dispatch(fetchFlavors());
    if (!securityGroupsLoaded) dispatch(fetchSecurityGroups());
    if (!securityRulesLoaded) dispatch(fetchSecurityGroupRules());
    if (!privateNetworkLoaded) dispatch(fetchPrivateNetwork());
    if (!volumesLoaded) dispatch(fetchVolumes());
    if (!billingLoaded) dispatch(fetchBillingForecast());
    // The budget banner's own read (BUDGET_MANDATORY_PLAN_2026-10-28 section 5.2): database only, never the forecast.
    if (!budgetStateLoaded) dispatch(fetchBudgetState());
    if (!snapshotsLoaded) dispatch(fetchSnapshots());
    if (!backupsLoaded) dispatch(fetchBackups());
    if (!loadBalancersLoaded) dispatch(fetchLoadBalancers());
    if (!regionsLoaded) dispatch(fetchRegions());
    if (!keypairsLoaded) dispatch(fetchKeypairs());
  }, [
    keycloakReady,
    isAuthenticated,
    osReady,
    instancesLoaded,
    imagesLoaded,
    flavorsLoaded,
    securityGroupsLoaded,
    securityRulesLoaded,
    privateNetworkLoaded,
    volumesLoaded,
    billingLoaded,
    budgetStateLoaded,
    snapshotsLoaded,
    backupsLoaded,
    loadBalancersLoaded,
    regionsLoaded,
    keypairsLoaded,
    dispatch
  ]);

  // 4) Clarity identify + tag once we have user + ready
  useEffect(() => {
    if (!keycloakReady || !isAuthenticated || !osReady) return;

    // Even if userInfo is unknown shape, this is safe
    safeClarityIdentifyAndTag(userInfo);
  }, [keycloakReady, isAuthenticated, osReady, userInfo]);

  const retryProvisioning = async () => {
    const kc = getKeycloakInstance();
    dispatch(setProvisioningTimedOut(false));
    if (!kc || !kc.tokenParsed) return;

    const timeoutMs = 3 * 60 * 1000; // 3 minutes
    const intervalMs = 3000; // 3 seconds
    const startedAt = Date.now();

    let ready = Boolean((kc.tokenParsed as any).os_ready);
    while (!ready && (Date.now() - startedAt) < timeoutMs) {
      try {
        const refreshed = await kc.updateToken(36000);
        if (refreshed && kc.token && kc.tokenParsed) {
          const parsed = kc.tokenParsed as any;
          const nextUserInfo = parseKeycloakToken(parsed);

          dispatch(setAuthenticated({
            isAuthenticated: true,
            token: kc.token!,
            userInfo: nextUserInfo,
          }));

          ready = Boolean((kc.tokenParsed as any).os_ready);
          if (ready) break;
        }
      } catch (e) {
        // ignore and continue polling
      }
      await new Promise(res => setTimeout(res, intervalMs));
    }
    if (!ready) {
      dispatch(setProvisioningTimedOut(true));
    }
  };

  const handleLogout = async () => {
    try {
      const kc = getKeycloakInstance();
      await kc.logout();
    } finally {
      dispatch(logoutAction());
    }
  };

  // --- UI states ---
  if (keycloakError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Unable to connect to authentication service</div>
        <div style={{ maxWidth: 480, color: '#777', marginBottom: 20 }}>
          We could not reach the login service. This may be a temporary network issue.
          Please check your connection and try again, or contact support if the problem persists.
        </div>
        <button onClick={() => window.location.reload()} className="btn btn-primary">Retry</button>
      </div>
    );
  }

  if (!isAuthenticated || !keycloakReady) {
    return (
      <div className="fullscreen-center">
        <div className="fancyspinner">
          <div className="ring"></div>
          <div className="ringouter"></div>
          <div className="dot"></div>
        </div>
      </div>
    );
  }

  if (isAuthenticated && keycloakReady && !osReady) {
    if (!provisioningTimedOut) {
      return (
        <div className="fullscreen-center">
          <div className="fancyspinner">
            <div className="ring"></div>
            <div className="ringouter"></div>
            <div className="dot"></div>
          </div>
          <div style={{ marginTop: 16, color: '#888' }}>
            Setting up your account… This may take a few moments.
          </div>
        </div>
      );
    }

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: 24,
        textAlign: 'center'
      }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
          We’re having trouble setting up your account
        </div>
        <div style={{ maxWidth: 520, color: '#777', marginBottom: 20 }}>
          Provisioning didn’t complete within the expected time. This can happen if background setup failed or is still in progress.
          You can try again, or come back later. If the problem persists, please contact support.
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={retryProvisioning} className="btn btn-primary">Try again</button>
          <button onClick={handleLogout} className="btn btn-secondary">Log out</button>
        </div>
      </div>
    );
  }

  // --- Main app ---
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <RouteTracker />
      <Navbar />
      <TrialBanner />
      <RegistrationNotices />
      <NotificationContainer />
      <div className="content-container">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/deploy" element={<StartPage />} />
          <Route path="/mcp" element={<McpPage />} />
          <Route path="/free-tier" element={<FreeTierPage />} />
          {/* The AI page became the MCP page (Milos 2026-10-26); old links and bookmarks land there */}
          <Route path="/ai" element={<Navigate to="/mcp" replace />} />
          <Route path="/category/user/*" element={<UserPage />} />
          <Route element={<PrivateRoute />}>
            <Route path="/category/compute/instances/create" element={<PaidInstanceLaunch><CreateInstancePage /></PaidInstanceLaunch>} />
          </Route>
          <Route element={<PrivateRoute requireRole="redu-admin" />}>
            <Route path="/admin" element={<AdminPage />} />
          </Route>
          <Route path="/category/compute/instances/:instanceId" element={<InstanceDetailsPage />} />
          <Route path="/category/compute/instances/snapshots" element={<SnapshotsPage />} />
          <Route path="/category/compute/instances/backups" element={<BackupsPage />} />
          <Route path="/category/:category/*" element={<CategoryPage />} />
          <Route path="/coming-soon" element={<ComingSoon />} />
          <Route path="/server-error" element={<ServerErrorPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
};

export default App;
